import i18n from 'helpers/i18n';
import messages from './messages';
import { useLayoutEffect, useState } from 'react';
import { AccountHierarchy } from 'types/api/AccountHierarchy';
import { HierarchySearch, NodeOption } from './HierarchySearch';
import { TextButton } from 'components/Organization/TextButton';
import { TreeNode } from './TreeNode';
import { TreeNodeTag } from './TreeNodeTag';
import { useNodeCheckboxTree, createHierarchyOptions, NodeMap } from './utils';

import {
  Dialog,
  DialogTitle,
  DialogPanel,
  DialogBackdrop
} from '@headlessui/react';

interface HierarchyModalProps {
  /** The name of the account. */
  account: string | null;

  /** List of hierarchy nodes. */
  nodes: AccountHierarchy[];

  /** Map of node names. */
  nodeNameMap: NodeMap;

  /** Modal title. */
  title: string;

  /** Submit button label. */
  buttonText: string;

  /** Whether the modal is open. */
  isOpen: boolean;

  /** Node ids of current selected nodes. */
  selectedNodes: number[];

  /** Callback fired when clicking the submit button. */
  onSave: (nodes: number[]) => void;

  /** Callback fired when closing the modal. */
  onClose: () => void;
}

export function HierarchyModal({
  nodes,
  nodeNameMap,
  account,
  title,
  buttonText,
  selectedNodes,
  isOpen,
  onSave,
  onClose
}: HierarchyModalProps) {
  // If the first node is the only one, it's the root node so open it by default
  const defaultOpenNodes = nodes.length === 1 ? [nodes[0].id] : [];
  const [openNodes, setOpenNodes] = useState<number[]>(defaultOpenNodes);
  const [highlightedNodeId, setHighlightedNodeid] = useState<number | null>(
    null
  );

  const { nodeMap, updateNode, clearAll, checkedNodeIds } = useNodeCheckboxTree(
    nodes,
    selectedNodes
  );

  useLayoutEffect(() => {
    if (highlightedNodeId === null) return;
    const element = document.querySelector(
      `[data-hierarchy-id="${highlightedNodeId}"]`
    );

    element?.scrollIntoView();
  }, [highlightedNodeId]);

  const handleOpenNode = (nodeId: number) => {
    setOpenNodes(prev => {
      if (prev.includes(nodeId)) {
        return prev.filter(node => node !== nodeId);
      }

      return [...prev, nodeId];
    });
  };

  const handleNodeSelect = (node?: NodeOption | null) => {
    if (!node) {
      setHighlightedNodeid(null);
      return;
    }

    setOpenNodes(node.treeIds);
    setHighlightedNodeid(node.id);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50">
      <DialogBackdrop className="fixed inset-0 bg-black opacity-50" />

      <div className="fixed inset-0 flex items-center justify-center text-center font-sans text-black">
        <DialogPanel className="relative w-full max-w-4xl max-h-[95vh] flex flex-col bg-white shadow-xl rounded text-left">
          <DialogTitle
            as="h3"
            className="font-bold text-lg md:text-xl border-b border-[#D7DFE8] px-8 py-3 m-0"
          >
            {title} {account !== null && ` - ${account}`}
          </DialogTitle>
          <button
            className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
            onClick={onClose}
          >
            <i className="fa fa-times fa-lg"></i>
          </button>
          <div className="flex-1 overflow-y-auto">
            <div className="p-4">
              <HierarchySearch
                nodes={createHierarchyOptions(nodes, nodeNameMap)}
                onNodeSelect={handleNodeSelect}
              />
              <p className="text-sm mt-4">
                {i18n.ft(messages.hierarchyNotice)}
              </p>

              {checkedNodeIds.length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-2.5 items-start">
                  <div className="flex flex-wrap gap-1.5">
                    {checkedNodeIds
                      .filter(nodeId => nodeNameMap[nodeId] !== undefined) // Filtering out the ids that are not visible in the current user's hierarchy scope.
                      .map(nodeId => (
                        <TreeNodeTag
                          key={nodeId}
                          name={nodeNameMap[nodeId]?.tagName}
                          onRemove={() => updateNode(nodeId, false)}
                        />
                      ))}
                  </div>

                  <div className="ml-auto flex items-center gap-6 shrink-0">
                    <TextButton size="small" onClick={clearAll}>
                      <i className="fa fa-times mr-1" />
                      {i18n.ft(messages.clearAll)}
                    </TextButton>
                  </div>
                </div>
              )}
            </div>

            <div>
              {nodes.map((node: AccountHierarchy) => (
                <TreeNode
                  key={node.id}
                  node={node}
                  level={1}
                  checkedMap={nodeMap}
                  handleCheckboxChange={updateNode}
                  openNodes={openNodes}
                  onOpenNode={handleOpenNode}
                  highlightedNodeId={highlightedNodeId}
                />
              ))}
            </div>
          </div>
          <div className="sticky bottom-0 bg-white border-t border-[#D7DFE8] px-6 py-4 flex justify-end rounded-b">
            <button
              className="px-4 py-2 bg-action font-semibold text-white rounded"
              onClick={() => onSave(checkedNodeIds)}
            >
              {buttonText}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
